import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { createAccessHelpers, roleHomePath } from '../lib/access';
import { resetPostAuthDestinationLock, clearIntendedRedirect } from '../lib/intendedRedirect';

const AuthContext = createContext(null);

function parseApiError(error, fallback = 'Request failed') {
    const msg = error.response?.data?.message ?? error.message;
    if (typeof msg === 'string') return msg;
    if (msg && typeof msg === 'object' && msg.errors) {
        return Object.values(msg.errors).flat().join(' ');
    }
    return fallback;
}

function persistSession(token, userData, setUser) {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('auth_token');

        if (storedUser && token) {
            try {
                setUser(JSON.parse(storedUser));
                api.get('/me')
                    .then((response) => {
                        if (response.data.success && response.data.data) {
                            const fresh = response.data.data;
                            localStorage.setItem('user', JSON.stringify(fresh));
                            setUser(fresh);
                        }
                    })
                    .catch(() => {
                        localStorage.removeItem('user');
                        localStorage.removeItem('auth_token');
                        setUser(null);
                    });
            } catch {
                localStorage.removeItem('user');
                localStorage.removeItem('auth_token');
            }
        }
        setLoading(false);
    }, []);

    const requestLogin = useCallback(async (identifier) => {
        try {
            const response = await api.post('/login', { identifier });

            if (!response.data.success) {
                throw new Error(
                    typeof response.data.message === 'string' ? response.data.message : 'Login failed'
                );
            }

            const data = response.data.data || {};

            if (data.requires_otp) {
                return {
                    requiresOtp: true,
                    identifier: data.identifier || identifier,
                    maskedEmail: data.masked_email,
                    message: response.data.message,
                    resendAfter: data.resend_after ?? 60,
                };
            }

            if (data.token && data.user) {
                persistSession(data.token, data.user, setUser);
                return {
                    requiresOtp: false,
                    user: data.user,
                    data,
                };
            }

            throw new Error(
                typeof response.data.message === 'string' ? response.data.message : 'Login failed'
            );
        } catch (error) {
            throw new Error(parseApiError(error, 'Login failed'));
        }
    }, []);

    const verifyLoginOtp = useCallback(async (identifier, otp) => {
        try {
            const response = await api.post('/login/verify-otp', { identifier, otp });

            if (response.data.success && response.data.data?.token) {
                persistSession(response.data.data.token, response.data.data.user, setUser);
                return response.data;
            }

            throw new Error(
                typeof response.data.message === 'string' ? response.data.message : 'Verification failed'
            );
        } catch (error) {
            throw new Error(parseApiError(error, 'Verification failed'));
        }
    }, []);

    const resendLoginOtp = useCallback(async (identifier) => {
        try {
            const response = await api.post('/login/resend-otp', { identifier });
            if (!response.data.success) {
                throw new Error(
                    typeof response.data.message === 'string' ? response.data.message : 'Could not resend code'
                );
            }
            return {
                message: response.data.message,
                resendAfter: response.data.data?.resend_after ?? 60,
                maskedEmail: response.data.data?.masked_email,
            };
        } catch (error) {
            throw new Error(parseApiError(error, 'Could not resend code'));
        }
    }, []);

    const login = useCallback(async (identifier) => requestLogin(identifier), [requestLogin]);

    const loginWithToken = useCallback(async (token) => {
        localStorage.setItem('auth_token', token);
        const response = await api.get('/me');
        if (response.data.success && response.data.data) {
            const userData = response.data.data;
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            return userData;
        }
        localStorage.removeItem('auth_token');
        throw new Error('Failed to authenticate with KingsChat session');
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post('/logout');
        } catch {
            // Clear local session anyway
        }
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        clearIntendedRedirect();
        resetPostAuthDestinationLock();
        setUser(null);
    }, []);

    const refreshUser = useCallback(async () => {
        const response = await api.get('/me');
        if (response.data.success && response.data.data) {
            localStorage.setItem('user', JSON.stringify(response.data.data));
            setUser(response.data.data);
            return response.data.data;
        }
        return null;
    }, []);

    const access = useMemo(() => createAccessHelpers(user), [user]);

    const value = useMemo(
        () => ({
            user,
            setUser,
            loading,
            isAuthenticated: !!user,
            role: access.primaryRole,
            roles: access.roles,
            permissions: access.permissions,
            portal: access.portal,
            homePath: access.homePath,
            menuItems: access.menuItems,
            isSuper: access.isSuper,
            hasRole: access.hasRole,
            hasAnyRole: access.hasAnyRole,
            hasPermission: access.hasPermission,
            hasAnyPermission: access.hasAnyPermission,
            canAccess: access.canAccess,
            login,
            requestLogin,
            verifyLoginOtp,
            resendLoginOtp,
            loginWithToken,
            logout,
            refreshUser,
        }),
        [
            user,
            loading,
            access,
            login,
            requestLogin,
            verifyLoginOtp,
            resendLoginOtp,
            loginWithToken,
            logout,
            refreshUser,
        ]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export { roleHomePath };
